import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api.ts";
import { relativeDate } from "../lib/format.ts";
import { navigate, onLink } from "../lib/router.ts";
import { useGet, useSession } from "../lib/session.tsx";

interface Conn {
  id: string;
  accountId: string;
  accountName: string;
  clientName: string;
  scope: "ler" | "operar" | "construir";
  createdAt: string;
  lastUsedAt: string | null;
  revokedAt: string | null;
}

const SCOPE = { ler: "só leitura", operar: "operar dados", construir: "construir o sistema" } as const;

type Client = "claude" | "chatgpt" | "cursor" | "vscode" | "claude-code";
const CLIENTS: { id: Client; label: string }[] = [
  { id: "claude", label: "Claude" },
  { id: "chatgpt", label: "ChatGPT" },
  { id: "cursor", label: "Cursor" },
  { id: "vscode", label: "VS Code" },
  { id: "claude-code", label: "Claude Code" },
];
const CLIENT_KEY = "vj_ai_client";

function savedClient(): Client {
  try {
    const v = localStorage.getItem(CLIENT_KEY);
    if (CLIENTS.some((c) => c.id === v)) return v as Client;
  } catch {
    /* private window: start on Claude */
  }
  return "claude";
}

/**
 * "Conexões de IA": a step-by-step guide to plug the person's own AI into
 * mcp.varejoon.com (milestone 17), then the connections to this store.
 * While the person has no connection, the page checks every few seconds,
 * so it moves to the first request as soon as the AI signs in.
 */
export function Connections() {
  const s = useSession();
  const { data, error, reload } = useGet<{ connections: Conn[]; mcpUrl: string }>("/v1/connections");
  const [client, setClient] = useState<Client>(savedClient);
  const url = data?.mcpUrl ?? "https://mcp.varejoon.com";
  const mine = data?.connections.filter((c) => c.accountId === s.account.id && !c.revokedAt) ?? [];
  const connected = mine.length > 0;

  // Wait for the first connection: poll while the tab is visible.
  const seen = useRef<boolean | null>(null);
  useEffect(() => {
    if (!data || connected) return;
    const t = setInterval(() => document.visibilityState === "visible" && reload(), 4000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, connected]);
  useEffect(() => {
    if (!data) return;
    if (seen.current === false && connected) s.toast(`${mine[0]!.clientName} conectado ao Varejoon.`);
    seen.current = connected;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, connected]);

  function pick(c: Client) {
    setClient(c);
    try {
      localStorage.setItem(CLIENT_KEY, c);
    } catch {
      /* not remembered, that is all */
    }
  }

  async function revoke(c: Conn) {
    if (!confirm(`Revogar a conexão ${c.clientName}? A IA perde o acesso na hora.`)) return;
    await api("DELETE", `/v1/connections/${c.id}`);
    s.toast("Conexão revogada.");
    reload();
  }

  const copy = (text: string, what: string) =>
    navigator.clipboard?.writeText(text).then(
      () => s.toast(`${what} copiado.`),
      () => s.toast("Não deu para copiar. Selecione o texto e copie."),
    );

  return (
    <div className="stack" style={{ maxWidth: 820 }}>
      <div className="topbar">
        <h1>Conexões de IA</h1>
      </div>

      <section className="card stack cx">
        <div className="cx-head">
          <div>
            <strong>Conecte a sua IA ao Varejoon</strong>
            <p className="muted" style={{ margin: "2px 0 0" }}>
              Leva uns dois minutos. Depois é só pedir: a IA monta coleções, telas e automações e responde perguntas sobre a loja.
            </p>
          </div>
          <span className={`chip ${connected ? "green" : "gray"}`}>{connected ? "Conectada" : data ? "Aguardando conexão" : "…"}</span>
        </div>

        <div className="seg cx-seg" role="group" aria-label="Qual IA você usa">
          {CLIENTS.map((c) => (
            <button key={c.id} type="button" aria-pressed={client === c.id} onClick={() => pick(c.id)}>
              {c.label}
            </button>
          ))}
        </div>

        <ol className="cx-steps">
          <Steps client={client} url={url} copy={copy} />
          <li className={connected ? "done" : ""}>
            <b>Entre com sua conta Varejoon</b>
            <span>
              A IA abre uma página do Varejoon. Entre com o código do e-mail ou com o Google, escolha as lojas e o nível de acesso: só leitura, operar
              dados ou construir o sistema.
            </span>
            {connected ? (
              <span className="cx-ok">
                Conectada: {mine.map((c) => `${c.clientName} (${SCOPE[c.scope] ?? c.scope})`).join(", ")}.
              </span>
            ) : (
              data && (
                <span className="cx-wait">
                  <i /> Esta página avança sozinha quando a conexão chegar.
                </span>
              )
            )}
          </li>
          <li className={connected ? "now" : ""}>
            <b>Faça o primeiro pedido</b>
            <span>Abra uma conversa nova, ligue o Varejoon nas ferramentas e peça. Toque num exemplo para copiar:</span>
            <div className="cx-asks">
              {asks(s.tenant?.name ?? "minha loja", s.collections.length > 0).map((a) => (
                <button key={a} type="button" className="cx-ask" onClick={() => copy(a, "Pedido")}>
                  {a}
                </button>
              ))}
            </div>
            <span className="muted">
              Mudanças na estrutura (coleções, telas, papéis, automações) não entram direto: a IA propõe e você aprova em{" "}
              <a href="/changesets" onClick={onLink}>
                Mudanças
              </a>
              . Dados mudam na hora, dentro do que o seu papel permite.
            </span>
          </li>
        </ol>
      </section>

      {error && <p className="error">{error.message}</p>}
      <div className="card list">
        <strong>Conexões com esta loja</strong>
        {data?.connections.map((c) => (
          <div key={c.id} className="list-item" style={{ opacity: c.revokedAt ? 0.6 : 1, flexWrap: "wrap" }}>
            <span style={{ fontWeight: 600 }}>{c.clientName}</span>
            <span className="chip gray">{SCOPE[c.scope] ?? c.scope}</span>
            {c.accountId !== s.account.id && <span className="muted">de {c.accountName}</span>}
            <span className="spacer" />
            <span className="muted" style={{ fontSize: 12.5 }}>
              {c.revokedAt ? `revogada em ${relativeDate(c.revokedAt)}` : c.lastUsedAt ? `usada em ${relativeDate(c.lastUsedAt)}` : `criada em ${relativeDate(c.createdAt)}`}
            </span>
            {!c.revokedAt && (
              <button className="btn sm danger" onClick={() => revoke(c)}>
                Revogar
              </button>
            )}
          </div>
        ))}
        {data && !data.connections.length && <p className="muted" style={{ margin: 0 }}>Nenhuma IA conectada ainda.</p>}
      </div>
    </div>
  );
}

function asks(store: string, hasData: boolean): string[] {
  const start = hasData
    ? [`No Varejoon, o que já existe na loja ${store}? Resuma as coleções e quantos registros há.`]
    : [`No Varejoon, monte para a loja ${store} um cadastro de produtos com nome, preço, custo e estoque, e uma tela para listar e editar.`];
  return [
    ...start,
    "No Varejoon, crie uma coleção de clientes com nome, telefone e aniversário, e cadastre estes três: …",
    "No Varejoon, quando o estoque de um produto ficar abaixo de 5, marque o produto como “repor”.",
  ];
}

/** The first step(s): where to put the address, for each AI. */
function Steps({ client, url, copy }: { client: Client; url: string; copy: (t: string, what: string) => void }) {
  const Url = () => <Copy text={url} what="Endereço" copy={copy} />;
  switch (client) {
    case "claude":
      return (
        <li>
          <b>Adicione o conector</b>
          <span>
            No claude.ai ou no app do Claude, abra <em>Configurações → Conectores</em> e toque em <em>Adicionar conector personalizado</em>. Dê o nome
            Varejoon e cole o endereço:
          </span>
          <Url />
          <span className="muted">No plano gratuito do Claude cabe um conector personalizado. Em Team e Enterprise, quem administra a organização adiciona primeiro.</span>
        </li>
      );
    case "chatgpt":
      return (
        <li>
          <b>Crie o app no ChatGPT</b>
          <span>
            No ChatGPT web, abra <em>Configurações → Apps e conectores → Configurações avançadas</em> e ligue o <em>Modo desenvolvedor</em>. Depois, em{" "}
            <em>Apps e conectores</em>, toque em <em>Criar</em>, dê o nome Varejoon, cole o endereço e escolha autenticação OAuth:
          </span>
          <Url />
          <span className="muted">
            Por enquanto o ChatGPT só deixa a IA escrever (criar registros, propor mudanças) nos planos Business, Enterprise e Edu. No Pro ela só lê.
          </span>
        </li>
      );
    case "cursor": {
      const json = JSON.stringify({ mcpServers: { varejoon: { url } } }, null, 2);
      return (
        <li>
          <b>Adicione o servidor MCP</b>
          <span>
            No Cursor, abra <em>Settings → MCP → Add new MCP server</em>, ou cole isto em <code>~/.cursor/mcp.json</code>:
          </span>
          <Copy text={json} what="Configuração" copy={copy} block />
        </li>
      );
    }
    case "vscode": {
      const json = JSON.stringify({ servers: { varejoon: { type: "http", url } } }, null, 2);
      return (
        <li>
          <b>Adicione o servidor MCP</b>
          <span>
            No VS Code, rode <em>MCP: Add Server</em> na paleta de comandos e escolha HTTP, ou cole isto em <code>.vscode/mcp.json</code>:
          </span>
          <Copy text={json} what="Configuração" copy={copy} block />
          <span className="muted">Use o Copilot Chat no modo agente para chamar as ferramentas do Varejoon.</span>
        </li>
      );
    }
    case "claude-code": {
      const cmd = `claude mcp add --transport http varejoon ${url}`;
      return (
        <li>
          <b>Adicione o servidor MCP</b>
          <span>No terminal, rode o comando abaixo. Depois, dentro do Claude Code, digite <code>/mcp</code> e escolha Varejoon para entrar.</span>
          <Copy text={cmd} what="Comando" copy={copy} block />
        </li>
      );
    }
  }
}

function Copy({ text, what, copy, block }: { text: string; what: string; copy: (t: string, what: string) => void; block?: boolean }) {
  return (
    <span className={`cx-copy ${block ? "block" : ""}`}>
      {block ? <pre>{text}</pre> : <code>{text}</code>}
      <button type="button" className="btn sm" onClick={() => copy(text, what)}>
        Copiar
      </button>
    </span>
  );
}

/** A nudge on the empty home screen and elsewhere: plug the AI in. */
export function ConnectNudge() {
  return (
    <div className="cx-nudge">
      <div>
        <strong>Monte a loja conversando com a sua IA</strong>
        <p className="muted" style={{ margin: "2px 0 0" }}>
          Conecte o Claude, o ChatGPT ou outra IA em dois minutos e peça o sistema do seu jeito.
        </p>
      </div>
      <button type="button" className="btn primary" onClick={() => navigate("/conexoes")}>
        Conectar minha IA
      </button>
    </div>
  );
}
